import { cn } from "@/lib/utils"
import { Textarea } from "@/components/ui/textarea"
import ChatInputFiles, { PastedFile } from "@/components/ChatInputFiles"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Check, Copy, Paperclip, SendHorizonal, Smile } from "lucide-react"
import * as React from "react"
import { useCallback, useContext, useEffect, useRef, useState } from "react"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  EmojiPicker,
  EmojiPickerContent,
  EmojiPickerFooter,
  EmojiPickerSearch,
} from "@/components/ui/emoji-picker"
import DeliveryOrderIdFromCommandResultParser from "@/lib/DeliveryOrderIdFromCommandResultParser"
import AdHocCommands, {
  AvoidType,
  DeliveryOrderMetadata,
  LoadType,
} from "@/api/adHocCommands"
import RouteGoogleLinkParser from "@/lib/routeGoogleLinkParser"
import ShareRouteParser from "@/lib/shareRouteParser"
import { MessageOptions } from "@/reducers/chatsSliceThunks"
import { useAnalyzeOrderContext } from "@/contexts/AnalyzeOrderContext"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { useAppSelector } from "@/reducers/hooks"
import { findRoute2g, Route2g, route2gEq } from "@/reducers/routes2gSlice"
import useCarRouteConfig from "@/hooks/useCarRouteConfig"
import { XmppContext } from "@/stream/xmppClient"
import { DriverContext } from "@/contexts/DriverContext"
import { selectAccountChat } from "@/reducers/accountChatsSelectors"
import { findProfile } from "@/reducers/profilesSliceSelectors"
import routeMessageTemplates from "@/lib/routeMessageTemplates.json"
import { shouldShowOrderMessageExtraNotes } from "@/lib/orderMessageHostRules"
import { useNaviConfig } from "@/hooks/useNaviConfig"
import { buildSharedRouteLink } from "@/lib/sharedRouteLink"
import { useStopToStopDistance } from "@/hooks/useStopToStopDistance"
import { updateDistanceInMessage } from "@/components/analyzeOrderOnPage/utils"
import { buildOrderRouteUpsert } from "@/lib/analyzeOrderRouteStops"
import { retrieveDriverRoute } from "@/api/xeps/Routes"
import { useTranslation } from "react-i18next"

const AnalyzeOrderMessageBox = ({
  route,
  onSend,
  avoid,
  loadType,
  onClose,
  vhost,
  showApproachDistance = false,
}: {
  route: Route2g | undefined
  onSend: (text: string, files: FileList, options?: MessageOptions) => void
  avoid: AvoidType[]
  loadType: LoadType[]
  onClose?: () => void
  vhost?: string
  showApproachDistance?: boolean
}) => {
  const { t } = useTranslation()
  const {
    driver,
    routeJid,
    setModalHidden,
    shouldSkipVerifyStep,
    routeData,
    messageId,
    files,
    wasEdited,
    messageText,
    setMessageText,
    handleCloseMap,
    originalRouteMetaData,
    routeMetaData,
    naviTransportId,
    setNaviTransportId,
    naviSelection,
    setNaviSelection,
    baseStopIds,
  } = useAnalyzeOrderContext()

  const { configured: naviConfigured } = useNaviConfig(vhost)

  const { client } = useContext(XmppContext)
  const { upsertRoute2g } = useContext(DriverContext)
  const inProduct = Boolean(window.XMPP && !window.anonymousXMPP)

  const accountChat = useAppSelector(selectAccountChat(routeJid ?? ""))
  const isDoubleCrew = accountChat?.group === "double_crew"
  const driverProfile = useAppSelector(findProfile(accountChat?.driverA))
  const preferredLanguage = driverProfile?.preferredLanguage || "pl"
  const templates =
    routeMessageTemplates[
      preferredLanguage as keyof typeof routeMessageTemplates
    ] || routeMessageTemplates.pl
  const name = driverProfile
    ? `${driverProfile.firstName} ${driverProfile.lastName}`
    : driver
      ? `${driver.firstName} ${driver.lastName}`
      : ""

  const [messageEditedAlready, setMessageEditedAlready] = useState(false)
  const isGoogleRouteLinkEnabled = useFeatureFlagEnabled("google-route-link")
  const isHereWeGoShareLinkEnabled = useFeatureFlagEnabled(
    "herewego-share-link",
  )
  const showLegDistance = useStopToStopDistance()
  const currentRoute = useAppSelector(findRoute2g(routeJid), route2gEq)
  const [isSending, setIsSending] = useState(false)
  const [copySuccessAnimate, setCopySuccessAnimate] = useState(false)
  const [pastedFiles, setPastedFiles] = useState<PastedFile[]>([])
  const [isHayStrawCargoSelected, setIsHayStrawCargoSelected] = useState(false)
  const [isEmojiPickerOpen, setEmojiPickerOpen] = useState<boolean>(false)
  const inputFileRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const trackedPresentedMessageId = useRef<string | null>(null)

  const openFileBrowser = () => {
    inputFileRef.current?.click()
  }

  const { updateRouteConfig } = useCarRouteConfig(client, routeJid)

  const handleSendMessage = useCallback(async () => {
    // Allow sending if we have either text or files, and we have a route and route
    if (
      (!messageText.trim() && pastedFiles.length === 0) ||
      !route ||
      !routeJid ||
      isSending
    )
      return

    setIsSending(true)
    try {
      // Hide the modal instead of closing it

      setModalHidden(true)

      // 0. Reconcile the plan with the driver's route as it is now, and tag
      // the order's own stops with the transport order timestamp. The plan
      // was built minutes ago: a stop the driver has completed since must not
      // come back, nor be saved into this order. Ask gps for the route rather
      // than trusting the store — a stop left out of the save is deleted.
      const liveRoute =
        (client
          ? await retrieveDriverRoute(client, routeJid).catch((error) => {
              console.error("Failed to fetch the driver's route", error)
              return undefined
            })
          : undefined) ?? currentRoute
      const upsert = buildOrderRouteUpsert({
        plannedStops: route.stops,
        baseStopIds,
        liveStops: liveRoute?.stops,
        // Current timestamp in milliseconds since epoch as string
        deliveryOrderTimestamp: Date.now().toString(),
      })

      await updateRouteConfig({
        avoid,
        loadType,
      })

      const meta = routeData?.routeMetaData
      const sourceFile = files?.[0]?.url || undefined
      const orderMetadata: DeliveryOrderMetadata | undefined = meta
        ? {
            ref_number: meta.ref_number || undefined,
            notes: meta.notes || undefined,
            pallet_exchange_notes: meta.pallet_exchange_notes || undefined,
            pallet_exchange: meta.pallet_exchange,
            temperature_min: meta.temperature_min,
            temperature_max: meta.temperature_max,
            weight: meta.weight || undefined,
            price_netto_eur: meta.price_netto_eur,
            nip: meta.nip,
            order_number: meta.order_number,
            transport_number: meta.transport_number,
            payment_deadline: meta.payment_deadline,
            customer_name: meta.customer_name,
            customer_address: meta.customer_address,
            customer_postal_code: meta.customer_postal_code,
            customer_contact: meta.customer_contact,
            customer_email: meta.customer_email,
            customer_phone: meta.customer_phone,
            recipient_name: meta.recipient_name,
            dispatch_location: meta.dispatch_location,
            driver_name: meta.driver_name,
            car_plates: meta.car_plates,
            trailer_plates: meta.trailer_plates,
            billing: meta.billing,
            source_file: sourceFile,
            loaded_kilometers: meta.loaded_kilometers,
            empty_kilometers: meta.empty_kilometers,
          }
        : sourceFile
          ? { source_file: sourceFile }
          : undefined

      const upsertResult = await upsertRoute2g(
        { ...route, stops: upsert.stops },
        routeJid,
        { useLatLon: true, sendRouteHandle: true, orderMetadata },
      )
      const parsedStops =
        DeliveryOrderIdFromCommandResultParser.parse(upsertResult)
      const deliveryOrderId = parsedStops?.find(
        (stop) => stop.deliveryOrderId,
      )?.deliveryOrderId

      // Rebuild the message distance lines from the finalized stop distances
      // returned by GPS. The preview route carries stale cumulative distances
      // (e.g. an arrived stop still shifts every subsequent remaining_distance),
      // so the numbers in messageText may differ from what GPS persisted.
      // updateDistanceInMessage re-anchors both the total-distance and approach
      // lines to the saved values before the message is sent.
      const finalizedStops = parsedStops?.filter(
        (s) => s.remaining_distance !== undefined,
      )
      const baseMessageText =
        finalizedStops && finalizedStops.length > 0
          ? updateDistanceInMessage(
              messageText,
              finalizedStops,
              liveRoute,
              preferredLanguage,
              showApproachDistance,
            )
          : messageText

      let routeToken =
        DeliveryOrderIdFromCommandResultParser.parseDeliverOrderRouteToken(
          upsertResult,
        )

      // If no route token from delivery order, fall back to manual share
      if (!routeToken) {
        const shareResult = await AdHocCommands.shareRoute(
          window.XMPP!,
          routeJid!,
        )
        routeToken = ShareRouteParser.parse(shareResult)?.secret
      }

      // 3. Send the message to the chat
      const fileList = new DataTransfer()
      pastedFiles.forEach((file) => {
        fileList.items.add(file.file)
      })

      let finalText = baseMessageText

      if (isGoogleRouteLinkEnabled) {
        const routeGoogleLinkResult = await AdHocCommands.getRouteGoogleLink(
          window.XMPP!,
          routeJid!,
        )
        const routeGoogleLink = RouteGoogleLinkParser.parse(
          routeGoogleLinkResult,
        )?.googleLink

        finalText = `${finalText}\n\n${routeGoogleLink}`
      }

      const baseUrl =
        import.meta.env.MODE === "production" &&
        window.location.hostname !== "app.trickswift.eu"
          ? "https://app.slickshift.ai/"
          : "https://app.trickswift.eu/"

      const link = buildSharedRouteLink(baseUrl, routeToken, {
        showLegDistance,
      })
      const linkLabel = templates.openRouteInSlick || "🚛 Open route in Slick"

      finalText = `${finalText}\n\n${linkLabel}: ${link}`

      if (isHereWeGoShareLinkEnabled) {
        const hereWeGoLabel =
          templates.openRouteInHereWeGo || "🚚 Open route in HereWeGo"
        const hereWeGoLink = `${import.meta.env.REACT_APP_API_PUBLIC_BASE_URL}/shared_route_here?secret=${routeToken}`
        finalText = `${finalText}\n\n${hereWeGoLabel}: ${hereWeGoLink}`
      }

      // Prepare delivery order metadata if available
      const messageOptions: MessageOptions = {
        skipRouteDetection: true,
        startTracking: true,
      }
      if (routeData?.routeMetaData && files && files.length > 0) {
        messageOptions.deliveryOrderMetadata = {
          originalMetadata: routeData.routeMetaData,
          files,
          deliveryOrderId,
        }
      }

      await onSend(finalText, fileList.files, messageOptions)

      // Track successful completion
      window.analytics.track("AnalyzeOrderMessageSent", {
        fileNames: files?.map((uploadedFile) => uploadedFile.name),
        fileUrls: files?.map((uploadedFile) => uploadedFile.url),
        messageId: messageId,
        routeStopsCount: route.stops.length,
        // Stops the plan carried but the driver had left before the save, and
        // stops added to the route meanwhile that the save kept.
        goneStopsDropped: upsert.goneStops.length,
        stopsAddedMeanwhileKept: upsert.addedMeanwhile.length,
        hasAdditionalFiles: pastedFiles.length > 0,
        additionalFilesCount: pastedFiles.length,
        finalText,
        hasRouteMetadata: !!routeData?.routeMetaData,
        avoidOptions: avoid,
        loadType: loadType,
        // Original extracted data
        originalRouteMetaData: originalRouteMetaData,
        // Editing information
        wasEdited: wasEdited,
        verifyStepSkipped: shouldSkipVerifyStep,
        editedRouteMetaData: wasEdited ? routeData?.routeMetaData : undefined,
      })

      // Push the route to GBOX as a corridor (best-effort, never blocks send).
      // Fires whenever Marcos is configured + a driver exists — independent of
      // whether an order was created in Marcos (the push targets the driver's
      // GBOX box, not the Marcos transport).
      if (naviConfigured && window.XMPP && routeJid && vhost) {
        try {
          await AdHocCommands.finalizeNaviOrder(window.XMPP, vhost, routeJid)
        } catch (e) {
          console.error("navi GBOX passage push failed (non-blocking)", e)
          window.appToaster.show({
            message: t("orders.analyzeOrderMessageBox.gboxPushFailed"),
            intent: "warning",
            timeout: 5000,
          })
        }
        // Reset created-order state regardless (avoid stale id across orders).
        setNaviTransportId(undefined)
        setNaviSelection(undefined)
      }

      // 4. Clear files
      setPastedFiles([])

      // Close the modal completely on success
      handleCloseMap(true)
    } catch (error) {
      console.error("Failed to send message and set route:", error)
      window.analytics.track("AnalyzeOrderFailed", {
        reason: "send_message_error",
        error: error instanceof Error ? error.message : "Unknown error",
        messageId: messageId,
        fileNames: files?.map((uploadedFile) => uploadedFile.name),
        fileUrls: files?.map((uploadedFile) => uploadedFile.url),
        routeStopsCount: route?.stops?.length || 0,
      })

      // Show the modal again so user can retry
      setModalHidden(false)

      // Show error toast with retry option
      window.appToaster.show({
        message: t("orders.analyzeOrderMessageBox.sendFailed"),
        intent: "danger",
        timeout: 5000,
        action: {
          text: t("common.retry"),
          onClick: () => handleSendMessage(),
        },
      })
    } finally {
      setIsSending(false)
    }
  }, [
    messageText,
    pastedFiles,
    route,
    driver,
    isSending,
    setModalHidden,
    upsertRoute2g,
    updateRouteConfig,
    avoid,
    loadType,
    isGoogleRouteLinkEnabled,
    isHereWeGoShareLinkEnabled,
    routeData,
    files,
    onSend,
    messageId,
    originalRouteMetaData,
    wasEdited,
    shouldSkipVerifyStep,
    handleCloseMap,
    currentRoute,
    client,
    baseStopIds,
    naviConfigured,
    naviTransportId,
    naviSelection,
    vhost,
    routeJid,
    setNaviTransportId,
    setNaviSelection,
    showApproachDistance,
    preferredLanguage,
    t,
  ])

  // In Marcos mode the order is created in the TMS and the route set there, so
  // we don't send to the driver — the dispatcher copies the message text
  // instead (e.g. to paste elsewhere). Copies the current textarea content.
  const copyMessage = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(messageText)
      setCopySuccessAnimate(true)
      setTimeout(() => setCopySuccessAnimate(false), 1500)
      window.analytics.track("AnalyzeOrderMessageCopied", { messageId })
    } catch (e) {
      console.error("Failed to copy message", e)
      window.appToaster.show({
        message: t("orders.analyzeOrderMessageBox.copyFailed"),
        intent: "danger",
        timeout: 3000,
      })
    }
  }, [messageText, messageId, t])

  // Track when message is presented to user
  useEffect(() => {
    if (messageId && trackedPresentedMessageId.current !== messageId) {
      trackedPresentedMessageId.current = messageId
      window.analytics.track("AnalyzeOrderMessagePresented", {
        messageId,
        messageText,
      })
    }
  }, [messageId, messageText])

  const updateFilesStates = (files: File[]) => {
    const maxFiles = 8
    if (pastedFiles.length >= maxFiles) {
      window.appToaster.show({
        message: t("orders.analyzeOrderMessageBox.tooManyFiles", {
          count: maxFiles,
        }),
        intent: "danger",
        timeout: 3000,
      })
      return
    }

    const remainingSpace = maxFiles - pastedFiles.length
    const filesToAdd = files.slice(0, remainingSpace)

    const newFiles = filesToAdd.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }))

    setPastedFiles((prevFiles) => {
      const newFilesArray = [...prevFiles, ...newFiles]
      return newFilesArray.slice(0, maxFiles)
    })
  }

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return

    const files = Array.from(e.target.files)
    updateFilesStates(files)
  }

  const handleOnPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (e.clipboardData.files && e.clipboardData.files.length !== 0) {
      e.preventDefault()
      const files = Array.from(e.clipboardData.files)
      updateFilesStates(files)
    }
  }

  const handleRemoveImage = (index: number) => {
    const updatedFiles = pastedFiles.filter((file, i) => i !== index)
    setPastedFiles(updatedFiles)
  }

  const handleEmojiSelect = (emoji: { emoji: string }) => {
    setEmojiPickerOpen(false)

    if (textareaRef.current) {
      const [start, end] = [
        textareaRef.current.selectionStart,
        textareaRef.current.selectionEnd,
      ]
      const newString =
        messageText.substring(0, start ? start : 0) +
        emoji.emoji +
        messageText.substring(end ? end : 0, messageText.length)
      setMessageText(newString)

      // Focus back to textarea and set cursor position after emoji
      setTimeout(() => {
        textareaRef.current?.focus()
        const newCursorPosition = (start ? start : 0) + emoji.emoji.length
        textareaRef.current?.setSelectionRange(
          newCursorPosition,
          newCursorPosition,
        )
      }, 0)
    } else {
      setMessageText(messageText + emoji.emoji)
      // Focus back to textarea
      setTimeout(() => {
        textareaRef.current?.focus()
      }, 0)
    }
  }

  const handleDriverTextChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessageText(e.target.value)
      if (!messageEditedAlready) {
        window.analytics.track("AnalyzeOrderMessageEdited", {
          messageId: messageId,
        })
        setMessageEditedAlready(true)
      }
    },
    [setMessageText, messageEditedAlready, messageId],
  )

  const hayStrawNotes = templates.hayStrawNotes
  const showHayStrawCargoOption =
    shouldShowOrderMessageExtraNotes(vhost) && Boolean(hayStrawNotes)

  const handleHayStrawCargoChange = useCallback(
    (checked: boolean) => {
      if (!hayStrawNotes) return

      setIsHayStrawCargoSelected(checked)
      setMessageText(
        checked
          ? messageText.includes(hayStrawNotes)
            ? messageText
            : `${messageText.trimEnd()}\n\n${hayStrawNotes}`
          : messageText
              .replace(`\n\n${hayStrawNotes}`, "")
              .replace(hayStrawNotes, "")
              .trimEnd(),
      )
    },
    [hayStrawNotes, messageText, setMessageText],
  )

  const EmojiPopover = ({ disabled }: { disabled?: boolean }) => {
    return (
      <Popover open={isEmojiPickerOpen} onOpenChange={setEmojiPickerOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            disabled={disabled}
            className="h-8 w-8"
          >
            <Smile className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="z-[300] w-auto p-0" side="top">
          <EmojiPicker className="h-80" onEmojiSelect={handleEmojiSelect}>
            <EmojiPickerSearch />
            <EmojiPickerContent />
            <EmojiPickerFooter />
          </EmojiPicker>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <div className="w-full border-t p-4 dark:bg-gray-900">
      <div className="flex w-full flex-col gap-2">
        {naviTransportId && (
          <div className="rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:bg-blue-950 dark:text-blue-200">
            {routeMetaData?.order_number
              ? t("orders.analyzeOrderMessageBox.marcosOrderWithReference", {
                  id: naviTransportId,
                  reference: routeMetaData.order_number,
                })
              : t("orders.analyzeOrderMessageBox.marcosOrder", {
                  id: naviTransportId,
                })}
          </div>
        )}
        {naviConfigured && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-5 py-4 text-center text-lg font-semibold leading-snug text-blue-900 dark:border-blue-700 dark:bg-blue-950 dark:text-blue-200">
            {t("orders.analyzeOrderMessageBox.setRouteInMarcos")}
          </div>
        )}
        {showHayStrawCargoOption && (
          <label className="flex items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2 text-sm">
            <span className="font-medium">
              {t("orders.analyzeOrderMessageBox.hayStrawCargo")}
            </span>
            <Switch
              checked={isHayStrawCargoSelected}
              onCheckedChange={handleHayStrawCargoChange}
              disabled={isSending}
            />
          </label>
        )}
        <div
          className={cn(
            "flex-grow",
            pastedFiles.length !== 0 && "rounded-sm border border-border",
          )}
        >
          <Textarea
            ref={textareaRef}
            value={messageText}
            onChange={handleDriverTextChange}
            onPaste={handleOnPaste}
            placeholder={t("orders.analyzeOrderMessageBox.messagePlaceholder")}
            className={cn(
              "font-inherit max-h-[300px] min-h-[25vh] resize-none md:text-sm",
              pastedFiles.length !== 0 && "border-none shadow-none",
            )}
          />

          {pastedFiles.length !== 0 && (
            <ChatInputFiles
              pastedFiles={pastedFiles}
              handleRemoveImage={handleRemoveImage}
            />
          )}
        </div>

        <div className="flex items-center gap-2">
          {inProduct && onClose && (
            <Button variant="ghost" onClick={onClose}>
              {t("common.close")}
            </Button>
          )}
          <div className="flex-1" />

          <Button
            variant="ghost"
            size="icon"
            disabled={isSending}
            onClick={openFileBrowser}
            className="h-8 w-8"
          >
            <Paperclip className="h-4 w-4" />
          </Button>

          <input
            type="file"
            multiple={true}
            onChange={onFileChange}
            className="hidden"
            ref={inputFileRef}
            onClick={(e) => {
              e.currentTarget.value = ""
            }}
          />

          <EmojiPopover disabled={isSending} />

          {naviConfigured ? (
            <Button
              className="bg-blue-600 px-6 text-white hover:bg-blue-700"
              onClick={copyMessage}
              disabled={!messageText.trim()}
            >
              {copySuccessAnimate ? (
                <Check className="mr-2 h-4 w-4 duration-200 animate-in zoom-in" />
              ) : (
                <Copy className="mr-2 h-4 w-4" />
              )}
              {t("orders.analyzeOrderMessageBox.copyMessage")}
            </Button>
          ) : (
            <Button
              className="bg-blue-600 px-6 text-white hover:bg-blue-700"
              onClick={handleSendMessage}
              disabled={
                (!messageText.trim() && pastedFiles.length === 0) ||
                !route ||
                isSending
              }
            >
              <SendHorizonal className="mr-2 h-4 w-4" />
              {isSending
                ? t("orders.analyzeOrderMessageBox.sending")
                : isDoubleCrew
                  ? t("orders.analyzeOrderMessageBox.sendToDrivers")
                  : t("orders.analyzeOrderMessageBox.sendTo", { name })}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default AnalyzeOrderMessageBox
